import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API_URL = `${API_BASE}/api/yin-yoga`;

const resolveImageSrc = (value) => {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${API_BASE}${value}`;
  return `${API_BASE}/uploads/${value}`;
};

// ---------- Default empty objects for array fields ----------
const defaultModule = () => ({
  img: null,
  title: "",
  desc: "",
  tags: []
});

const defaultPhilosophy = () => ({
  img: null,
  title: "",
  text: ""
});

const defaultWhoItem = () => ({
  img: null,
  label: ""
});

const defaultAnatomyPoint = () => ({
  strong: "",
  text: ""
});

const defaultStat = () => ({
  num: "",
  label: ""
});

// ---------- Empty form template ----------
const createEmptyForm = () => ({
  heroEyebrow: "50-Hour Certified Training",
  heroTitle: "The Art of <em>Yin Yoga</em><br />Still. Deep. Transformative.",
  heroSubtitle: "A slow, meditative journey into the deep connective tissues of the body — bridging traditional yogic wisdom with modern anatomical understanding.",
  heroImage: null,
  heroBadges: ["50 Hours", "50+ Poses", "All Levels", "Certificate"],
  stats: [
    { num: "50+", label: "Yoga Poses" },
    { num: "6", label: "Core Modules" },
    { num: "12", label: "Meridians Covered" },
    { num: "∞", label: "Lifetime Access" }
  ],
  modules: [],
  poses: [
    "Melting Heart", "Banana Pose", "Butterfly", "Half Butterfly",
    "Camel Pose", "Caterpillar", "Child's Pose", "Dragon Pose",
    "Frog Pose", "Happy Baby", "Spinal Twist", "Saddle Pose",
    "Shoelace", "Snail Pose", "Sphinx & Seal", "Swan & Sleeping Swan",
    "Square Pose", "Squat Pose", "Toe Squat", "Straddle Pose", "Shavasana"
  ],
  philosophy: [],
  anatomyPoints: [
    { strong: "Connective Tissue & Fascia", text: " — understand how slow, sustained holds reshape deep tissues beyond muscle reach." },
    { strong: "Joint Complexes", text: " — map the shoulder, hip, spine, and knee for safe, effective sequencing." },
    { strong: "Compression vs Tension", text: " — learn to distinguish bony limits from soft-tissue stretch and teach accordingly." },
    { strong: "Pelvis as Emotional Center", text: " — explore the psycho-somatic relationship stored in the pelvic bowl." },
    { strong: "Trauma Release", text: " — discover how Yin postures can gently facilitate the release of held tension." }
  ],
  whoCanJoin: [],
  cta: {
    title: "Begin Your Journey into<br />Deep Stillness",
    description: "Join our 50-Hour Yin Yoga Training and gain the knowledge, confidence, and practice to transform yourself — and the students you teach.",
    buttonText: "Enroll Now →",
    buttonLink: "#",
    image: null
  },
  aboutSectionLabel: "What You'll Learn",
  aboutSectionTitle: "Course Modules",
  aboutSectionDescription: "A complete, deeply layered curriculum spanning breathwork, philosophy, anatomy, postures, meditation, and professional teaching skills.",
  philosophySectionLabel: "Ancient Wisdom",
  philosophySectionTitle: "Yin Yoga Philosophy",
  philosophySectionDescription: "Rooted in Taoist philosophy and yogic tradition, Yin Yoga offers a rich map of the energetic body and our relationship with the universe.",
  anatomySectionLabel: "Science of Stillness",
  anatomySectionTitle: "Understanding the Body in Yin",
  anatomyIntro: "Yin Yoga works far below the muscular layer. This module builds a thorough anatomical vocabulary so you can practice and teach with precision and care.",
  whoSectionLabel: "Open to All",
  whoSectionTitle: "Who Can Join?",
  whoSectionDescription: "No prior experience is required. This training welcomes anyone drawn to the healing, stillness, and depth that Yin Yoga offers.",
  ctaSectionLabel: "Start Today",
  ctaSectionTitle: "Begin Your Journey into<br />Deep Stillness",
  ctaDescription: "Join our 50-Hour Yin Yoga Training and gain the knowledge, confidence, and practice to transform yourself — and the students you teach."
});

const normalizeFormData = (item) => {
  const base = createEmptyForm();

  if (!item) {
    return base;
  }

  return {
    ...base,
    ...item,
    heroBadges: Array.isArray(item.heroBadges) ? [...item.heroBadges] : [...base.heroBadges],
    stats: Array.isArray(item.stats)
      ? item.stats.map((stat) => ({ ...defaultStat(), ...stat }))
      : base.stats.map((stat) => ({ ...stat })),
    modules: Array.isArray(item.modules)
      ? item.modules.map((mod) => ({
        ...defaultModule(),
        ...mod,
        tags: Array.isArray(mod?.tags) ? [...mod.tags] : []
      }))
      : [],
    poses: Array.isArray(item.poses) ? [...item.poses] : [...base.poses],
    philosophy: Array.isArray(item.philosophy)
      ? item.philosophy.map((entry) => ({
        ...defaultPhilosophy(),
        ...entry
      }))
      : [],
    anatomyPoints: Array.isArray(item.anatomyPoints)
      ? item.anatomyPoints.map((entry) => ({
        ...defaultAnatomyPoint(),
        ...entry
      }))
      : base.anatomyPoints.map((entry) => ({ ...entry })),
    whoCanJoin: Array.isArray(item.whoCanJoin)
      ? item.whoCanJoin.map((entry) => ({
        ...defaultWhoItem(),
        ...entry
      }))
      : [],
    cta: {
      ...base.cta,
      ...(item.cta || {})
    }
  };
};

// ---------- Reusable section wrapper ----------
const Section = ({ title, children }) => (
  <div className="card mb-4 shadow-sm">
    <div className="card-header bg-light fw-bold border-start border-4 border-primary">
      <h5 className="mb-0">{title}</h5>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

export default function YinYogaAdminForm() {
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(() => createEmptyForm());
  const [activeTab, setActiveTab] = useState("hero");
  const [imagePreviews, setImagePreviews] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recordCount, setRecordCount] = useState(0);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      const items = Array.isArray(res.data?.data) ? res.data.data : [];
      const current = res.data?.current || items[0] || null;

      setRecordCount(items.length);
      setEditId(current?._id || null);
      setFormData(normalizeFormData(current));
      setImagePreviews({});
    } catch (err) {
      console.error("Fetch error:", err);
      setRecordCount(0);
      setEditId(null);
      setFormData(createEmptyForm());
      setImagePreviews({});
    } finally {
      setLoading(false);
    }
  };

  // ---------- Generic change handlers ----------
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // For array of objects
  const handleArrayChange = (field, index, key, value) => {
    const arr = [...(formData[field] || [])];
    if (!arr[index]) arr[index] = {};
    arr[index] = { ...arr[index], [key]: value };
    setFormData({ ...formData, [field]: arr });
  };

  // For array of strings (e.g., poses, heroBadges)
  const handleStringArrayChange = (field, index, value) => {
    const arr = [...(formData[field] || [])];
    arr[index] = value;
    setFormData({ ...formData, [field]: arr });
  };

  // Add/remove array items
  const addArrayItem = (field, defaultItem) => {
    const arr = Array.isArray(formData[field]) ? [...formData[field]] : [];
    setFormData({ ...formData, [field]: [...arr, defaultItem()] });
  };

  const removeArrayItem = (field, index) => {
    const arr = [...(formData[field] || [])];
    arr.splice(index, 1);
    setFormData({ ...formData, [field]: arr });
  };

  // ---------- File handling ----------
  const handleFileChange = (field, e, subField = null, index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    // Generate preview URL
    const previewUrl = URL.createObjectURL(file);

    if (subField === "cta" && field === "image") {
      // Special case for cta.image
      setFormData({
        ...formData,
        cta: { ...formData.cta, image: file }
      });
      setImagePreviews({ ...imagePreviews, ctaImage: previewUrl });
    } else if (index !== null && subField) {
      // Array field with object (modules, philosophy, whoCanJoin)
      const arr = [...(formData[field] || [])];
      arr[index] = { ...arr[index], [subField]: file };
      setFormData({ ...formData, [field]: arr });
      // Store preview using a unique key
      setImagePreviews({ ...imagePreviews, [`${field}_${index}_${subField}`]: previewUrl });
    } else if (field === "heroImage") {
      setFormData({ ...formData, heroImage: file });
      setImagePreviews({ ...imagePreviews, heroImage: previewUrl });
    }
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formDataToSend = new FormData();

    // Append all top-level fields (except objects/arrays)
    Object.keys(formData).forEach(key => {
      if (key === "heroImage") {
        if (formData.heroImage instanceof File) {
          formDataToSend.append("heroImage", formData.heroImage);
        } else if (typeof formData.heroImage === "string") {
          formDataToSend.append("heroImage", formData.heroImage); // keep existing URL
        }
      } else if (key === "cta" || key === "modules" || key === "philosophy" || key === "whoCanJoin") {
        // cta is an object, handle inside
      } else if (Array.isArray(formData[key])) {
        // Arrays will be stringified
        formDataToSend.append(key, JSON.stringify(formData[key]));
      } else if (typeof formData[key] !== "object") {
        formDataToSend.append(key, formData[key]);
      }
    });

    // Handle cta object
    const ctaObj = { ...formData.cta };
    if (ctaObj.image instanceof File) {
      formDataToSend.append("ctaImage", ctaObj.image);
      delete ctaObj.image; // remove file from object before stringifying
    }
    formDataToSend.append("cta", JSON.stringify(ctaObj));

    // For arrays containing images (modules, philosophy, whoCanJoin),
    // we need to send images separately and replace the img field with a placeholder
    // that the backend will replace with the saved path. We'll send a combined JSON
    // and also append each image file with a special key.
    // Approach: Stringify the array, and separately append each image file with a reference.
    // Simpler: For each image field, we append the file and send the array with img set to a temp ID,
    // then backend processes accordingly. This requires backend support.
    // For simplicity, we'll assume backend accepts images as separate fields and updates the array.
    // We'll send arrays as JSON and images as separate fields with names like "module_img_0", etc.

    // For each module image
    formData.modules.forEach((mod, idx) => {
      if (mod.img instanceof File) {
        formDataToSend.append(`module_img_${idx}`, mod.img);
      }
    });
    // For each philosophy image
    formData.philosophy.forEach((ph, idx) => {
      if (ph.img instanceof File) {
        formDataToSend.append(`philosophy_img_${idx}`, ph.img);
      }
    });
    // For each whoCanJoin image
    formData.whoCanJoin.forEach((who, idx) => {
      if (who.img instanceof File) {
        formDataToSend.append(`who_img_${idx}`, who.img);
      }
    });

    // Remove img from arrays before stringifying to avoid sending huge base64
    const cleanModules = formData.modules.map(m => ({ ...m, img: m.img instanceof File ? "" : m.img }));
    const cleanPhilosophy = formData.philosophy.map(p => ({ ...p, img: p.img instanceof File ? "" : p.img }));
    const cleanWho = formData.whoCanJoin.map(w => ({ ...w, img: w.img instanceof File ? "" : w.img }));

    formDataToSend.append("modules", JSON.stringify(cleanModules));
    formDataToSend.append("philosophy", JSON.stringify(cleanPhilosophy));
    formDataToSend.append("whoCanJoin", JSON.stringify(cleanWho));

    try {
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      } else {
        await axios.post(API_URL, formDataToSend, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }
      await fetchData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error saving");
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "hero", label: "Hero" },
    { id: "stats", label: "Stats" },
    { id: "modules", label: "Modules" },
    { id: "poses", label: "Poses" },
    { id: "philosophy", label: "Philosophy" },
    { id: "anatomy", label: "Anatomy" },
    { id: "who", label: "Who Can Join" },
    { id: "cta", label: "CTA" }
  ];

  // Helper to get image preview
  const getPreview = (field, sub = null, index = null) => {
    if (sub === "cta" && field === "image") return imagePreviews.ctaImage;
    if (index !== null && sub) return imagePreviews[`${field}_${index}_${sub}`];
    if (field === "heroImage") return imagePreviews.heroImage;
    return null;
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-primary">Yin Yoga Page Content</h2>
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={fetchData}
          disabled={loading || saving}
        >
          <i className="bi bi-arrow-clockwise me-2"></i>Reload Saved Content
        </button>
      </div>

      {recordCount > 1 && !loading && (
        <div className="alert alert-warning">
          Multiple Yin Yoga records already exist. This form now edits the latest saved record only, and future saves will keep using that single record.
        </div>
      )}

      {!editId && !loading && (
        <div className="alert alert-info">
          No Yin Yoga content exists yet. Saving this form will create the single record used by both admin and frontend.
        </div>
      )}

      {loading ? (
        <div className="card shadow-sm">
          <div className="card-body py-5 text-center text-muted">Loading Yin Yoga content...</div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <ul className="nav nav-tabs mb-4 flex-wrap">
            {tabs.map(tab => (
              <li className="nav-item" key={tab.id}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === tab.id ? 'active fw-bold' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {/* ---------- Hero Tab ---------- */}
          {activeTab === "hero" && (
            <Section title="Hero Section">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Eyebrow Text</label>
                  <input type="text" name="heroEyebrow" className="form-control" value={formData.heroEyebrow} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Hero Image</label>
                  <input type="file" accept="image/*" className="form-control" onChange={(e) => handleFileChange("heroImage", e)} />
                  {getPreview("heroImage") && <img src={getPreview("heroImage")} alt="preview" className="mt-2 rounded" style={{ maxHeight: "100px" }} />}
                  {!getPreview("heroImage") && formData.heroImage && typeof formData.heroImage === "string" && (
                    <img src={resolveImageSrc(formData.heroImage)} alt="existing" className="mt-2 rounded" style={{ maxHeight: "100px" }} />
                  )}
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Hero Title (HTML allowed)</label>
                <textarea name="heroTitle" className="form-control" rows={3} value={formData.heroTitle} onChange={handleChange} />
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Hero Subtitle</label>
                <textarea name="heroSubtitle" className="form-control" rows={3} value={formData.heroSubtitle} onChange={handleChange} />
              </div>
              <label className="form-label fw-semibold">Badges</label>
              {(formData.heroBadges || []).map((badge, i) => (
                <div key={i} className="input-group mb-2">
                  <input type="text" className="form-control" value={badge} onChange={(e) => handleStringArrayChange("heroBadges", i, e.target.value)} />
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeArrayItem("heroBadges", i)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary" onClick={() => addArrayItem("heroBadges", () => "New Badge")}>Add Badge</button>
            </Section>
          )}

          {/* ---------- Stats Tab ---------- */}
          {activeTab === "stats" && (
            <Section title="Stats">
              <p className="text-muted">Four stats with number and label.</p>
              {formData.stats.map((stat, i) => (
                <div key={i} className="row border p-2 mb-2 rounded">
                  <div className="col-md-5">
                    <input type="text" className="form-control" placeholder="Number (e.g. 50+)" value={stat.num} onChange={(e) => handleArrayChange("stats", i, "num", e.target.value)} />
                  </div>
                  <div className="col-md-5">
                    <input type="text" className="form-control" placeholder="Label (e.g. Yoga Poses)" value={stat.label} onChange={(e) => handleArrayChange("stats", i, "label", e.target.value)} />
                  </div>
                  <div className="col-md-2">
                    <button type="button" className="btn btn-outline-danger" onClick={() => removeArrayItem("stats", i)}>Remove</button>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary" onClick={() => addArrayItem("stats", () => ({ num: "", label: "" }))}>Add Stat</button>
            </Section>
          )}

          {/* ---------- Modules Tab ---------- */}
          {activeTab === "modules" && (
            <Section title="Course Modules">
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Section Label</label>
                  <input type="text" name="aboutSectionLabel" className="form-control" value={formData.aboutSectionLabel} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Section Title</label>
                  <input type="text" name="aboutSectionTitle" className="form-control" value={formData.aboutSectionTitle} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Section Description</label>
                  <input type="text" name="aboutSectionDescription" className="form-control" value={formData.aboutSectionDescription} onChange={handleChange} />
                </div>
              </div>
              <hr />
              <label className="form-label fw-bold">Modules</label>
              {formData.modules.map((mod, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <label>Image</label>
                      <input type="file" accept="image/*" className="form-control" onChange={(e) => handleFileChange("modules", e, "img", i)} />
                      {getPreview("modules", "img", i) && <img src={getPreview("modules", "img", i)} alt="preview" className="mt-1" style={{ maxHeight: "60px" }} />}
                      {!getPreview("modules", "img", i) && mod.img && typeof mod.img === "string" && (
                        <img src={resolveImageSrc(mod.img)} alt="existing" className="mt-1" style={{ maxHeight: "60px" }} />
                      )}
                    </div>
                    <div className="col-md-8 mb-2">
                      <label>Title</label>
                      <input type="text" className="form-control" value={mod.title} onChange={(e) => handleArrayChange("modules", i, "title", e.target.value)} />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label>Description</label>
                    <textarea className="form-control" rows={2} value={mod.desc} onChange={(e) => handleArrayChange("modules", i, "desc", e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label>Tags (comma separated)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={(mod.tags || []).join(", ")}
                      onChange={(e) => handleArrayChange("modules", i, "tags", e.target.value.split(",").map(s => s.trim()))}
                    />
                  </div>
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeArrayItem("modules", i)}>Remove Module</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary" onClick={() => addArrayItem("modules", defaultModule)}>Add Module</button>
            </Section>
          )}

          {/* ---------- Poses Tab ---------- */}
          {activeTab === "poses" && (
            <Section title="Poses">
              <p className="text-muted">List of pose names (plain text).</p>
              {formData.poses.map((pose, i) => (
                <div key={i} className="input-group mb-2">
                  <input type="text" className="form-control" value={pose} onChange={(e) => handleStringArrayChange("poses", i, e.target.value)} />
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeArrayItem("poses", i)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary" onClick={() => addArrayItem("poses", () => "New Pose")}>Add Pose</button>
            </Section>
          )}

          {/* ---------- Philosophy Tab ---------- */}
          {activeTab === "philosophy" && (
            <Section title="Philosophy">
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Section Label</label>
                  <input type="text" name="philosophySectionLabel" className="form-control" value={formData.philosophySectionLabel} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Section Title</label>
                  <input type="text" name="philosophySectionTitle" className="form-control" value={formData.philosophySectionTitle} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Section Description</label>
                  <input type="text" name="philosophySectionDescription" className="form-control" value={formData.philosophySectionDescription} onChange={handleChange} />
                </div>
              </div>
              <hr />
              {formData.philosophy.map((ph, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <label>Image</label>
                      <input type="file" accept="image/*" className="form-control" onChange={(e) => handleFileChange("philosophy", e, "img", i)} />
                      {getPreview("philosophy", "img", i) && <img src={getPreview("philosophy", "img", i)} alt="preview" className="mt-1" style={{ maxHeight: "60px" }} />}
                      {!getPreview("philosophy", "img", i) && ph.img && typeof ph.img === "string" && (
                        <img src={resolveImageSrc(ph.img)} alt="existing" className="mt-1" style={{ maxHeight: "60px" }} />
                      )}
                    </div>
                    <div className="col-md-8 mb-2">
                      <label>Title</label>
                      <input type="text" className="form-control" value={ph.title} onChange={(e) => handleArrayChange("philosophy", i, "title", e.target.value)} />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label>Text</label>
                    <textarea className="form-control" rows={2} value={ph.text} onChange={(e) => handleArrayChange("philosophy", i, "text", e.target.value)} />
                  </div>
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeArrayItem("philosophy", i)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary" onClick={() => addArrayItem("philosophy", defaultPhilosophy)}>Add Philosophy Item</button>
            </Section>
          )}

          {/* ---------- Anatomy Tab ---------- */}
          {activeTab === "anatomy" && (
            <Section title="Anatomy">
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label">Section Label</label>
                  <input type="text" name="anatomySectionLabel" className="form-control" value={formData.anatomySectionLabel} onChange={handleChange} />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Section Title</label>
                  <input type="text" name="anatomySectionTitle" className="form-control" value={formData.anatomySectionTitle} onChange={handleChange} />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Intro Paragraph</label>
                <textarea name="anatomyIntro" className="form-control" rows={3} value={formData.anatomyIntro} onChange={handleChange} />
              </div>
              <hr />
              <label className="form-label fw-bold">Anatomy Points</label>
              {formData.anatomyPoints.map((point, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="mb-2">
                    <label>Strong Text</label>
                    <input type="text" className="form-control" value={point.strong} onChange={(e) => handleArrayChange("anatomyPoints", i, "strong", e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label>Description</label>
                    <input type="text" className="form-control" value={point.text} onChange={(e) => handleArrayChange("anatomyPoints", i, "text", e.target.value)} />
                  </div>
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeArrayItem("anatomyPoints", i)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary" onClick={() => addArrayItem("anatomyPoints", defaultAnatomyPoint)}>Add Point</button>
            </Section>
          )}

          {/* ---------- Who Can Join Tab ---------- */}
          {activeTab === "who" && (
            <Section title="Who Can Join">
              <div className="row mb-3">
                <div className="col-md-4">
                  <label className="form-label">Section Label</label>
                  <input type="text" name="whoSectionLabel" className="form-control" value={formData.whoSectionLabel} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Section Title</label>
                  <input type="text" name="whoSectionTitle" className="form-control" value={formData.whoSectionTitle} onChange={handleChange} />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Section Description</label>
                  <input type="text" name="whoSectionDescription" className="form-control" value={formData.whoSectionDescription} onChange={handleChange} />
                </div>
              </div>
              <hr />
              {formData.whoCanJoin.map((item, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <label>Image</label>
                      <input type="file" accept="image/*" className="form-control" onChange={(e) => handleFileChange("whoCanJoin", e, "img", i)} />
                      {getPreview("whoCanJoin", "img", i) && <img src={getPreview("whoCanJoin", "img", i)} alt="preview" className="mt-1" style={{ maxHeight: "60px" }} />}
                      {!getPreview("whoCanJoin", "img", i) && item.img && typeof item.img === "string" && (
                        <img src={resolveImageSrc(item.img)} alt="existing" className="mt-1" style={{ maxHeight: "60px" }} />
                      )}
                    </div>
                    <div className="col-md-6 mb-2">
                      <label>Label</label>
                      <input type="text" className="form-control" value={item.label} onChange={(e) => handleArrayChange("whoCanJoin", i, "label", e.target.value)} />
                    </div>
                  </div>
                  <button type="button" className="btn btn-outline-danger" onClick={() => removeArrayItem("whoCanJoin", i)}>Remove</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary" onClick={() => addArrayItem("whoCanJoin", defaultWhoItem)}>Add Item</button>
            </Section>
          )}

          {/* ---------- CTA Tab ---------- */}
          {activeTab === "cta" && (
            <Section title="Call to Action Banner">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Section Label</label>
                  <input type="text" name="ctaSectionLabel" className="form-control" value={formData.ctaSectionLabel} onChange={handleChange} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Section Title</label>
                  <input type="text" name="ctaSectionTitle" className="form-control" value={formData.ctaSectionTitle} onChange={handleChange} />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea name="ctaDescription" className="form-control" rows={2} value={formData.ctaDescription} onChange={handleChange} />
              </div>
              <hr />
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">CTA Title (HTML)</label>
                  <input type="text" className="form-control" value={formData.cta.title} onChange={(e) => setFormData({ ...formData, cta: { ...formData.cta, title: e.target.value } })} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">CTA Description</label>
                  <input type="text" className="form-control" value={formData.cta.description} onChange={(e) => setFormData({ ...formData, cta: { ...formData.cta, description: e.target.value } })} />
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Button Text</label>
                  <input type="text" className="form-control" value={formData.cta.buttonText} onChange={(e) => setFormData({ ...formData, cta: { ...formData.cta, buttonText: e.target.value } })} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Button Link</label>
                  <input type="text" className="form-control" value={formData.cta.buttonLink} onChange={(e) => setFormData({ ...formData, cta: { ...formData.cta, buttonLink: e.target.value } })} />
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">CTA Banner Image</label>
                <input type="file" accept="image/*" className="form-control" onChange={(e) => handleFileChange("image", e, "cta")} />
                {getPreview("image", "cta") && <img src={getPreview("image", "cta")} alt="preview" className="mt-2" style={{ maxHeight: "100px" }} />}
                {!getPreview("image", "cta") && formData.cta.image && typeof formData.cta.image === "string" && (
                  <img src={resolveImageSrc(formData.cta.image)} alt="existing" className="mt-2" style={{ maxHeight: "100px" }} />
                )}
              </div>
            </Section>
          )}

          <div className="mb-4">
            <button type="submit" className="btn btn-success btn-lg me-2" disabled={saving}>
              <i className="bi bi-save me-2"></i>{saving ? "Saving..." : editId ? "Update" : "Save"} Content
            </button>
            <button type="button" className="btn btn-secondary btn-lg" onClick={fetchData} disabled={saving}>
              <i className="bi bi-arrow-counterclockwise me-2"></i>Reset to Saved
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
